import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  getNotifications, 
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification 
} from '../service/apiService';
import { Notification, NotificationResponse, UnreadCountResponse } from '../types';
import { toast } from 'sonner';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoadingNotifications: boolean;
  fetchNotifications: (page?: number, status_filter?: 'unread' | 'read') => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (notificationId: string, createdAt: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotificationById: (notificationId: string, createdAt: string) => Promise<void>;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);

  const fetchNotifications = async (page: number = 1, status_filter?: 'unread' | 'read') => {
    setIsLoadingNotifications(true);
    try {
      const response: NotificationResponse = await getNotifications(page, 10, status_filter);
      
      if (page === 1) {
        setNotifications(response.notifications);
      } else {
        setNotifications(prev => [...prev, ...response.notifications]);
      }
      
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.total_pages);
      setHasNextPage(response.pagination.has_next);
      
      console.log('Notifications fetched:', response);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to fetch notifications');
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response: UnreadCountResponse = await getUnreadNotificationCount();
      setUnreadCount(response.unread_count);
      console.log('Unread count:', response.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const markAsRead = async (notificationId: string, createdAt: string) => {
    try {
      await markNotificationAsRead(createdAt);
      
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, status: 'read' as const, read_at: new Date().toISOString() }
            : notif
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      toast.success('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await markAllNotificationsAsRead();
      
      setNotifications(prev => 
        prev.map(notif => ({ 
          ...notif, 
          status: 'read' as const, 
          read_at: new Date().toISOString() 
        }))
      );
      
      setUnreadCount(0);
      
      toast.success(response.message || 'All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  const deleteNotificationById = async (notificationId: string, createdAt: string) => {
    try {
      await deleteNotification(createdAt);
      
      const deletedNotification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      
      if (deletedNotification?.status === 'unread') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  useEffect(() => {
  const handleAuthSuccess = async () => {
    try {
      await Promise.all([
        fetchNotifications(),
        fetchUnreadCount()
      ]);
      toast.success('Notifications refreshed successfully');
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    }
  };

  // Listen for custom auth success event
  window.addEventListener('authSuccess', handleAuthSuccess);
  
  return () => window.removeEventListener('authSuccess', handleAuthSuccess);
}, []);

  const contextValue: NotificationContextType = {
    notifications,
    unreadCount,
    isLoadingNotifications,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotificationById,
    currentPage,
    totalPages,
    hasNextPage
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};